import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "swiper/swiper-bundle.min.css";
import { getName } from "@/utils/functions/TokenFunction";
import Avatar from "@/components/cards/Avatar";
import ProfileDropdown from "@/components/dropdowns/ProfileDropdown";
import SwitchOrganizationModal from "@/components/modals/SwitchOrganizationModal";
import {
    DropdownMenu,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { OrganizationContext } from "@/context/providers/OrganizationProvider";
import { fetchConfig } from "@/services/setting.service";
import { errorToasterMessage } from "@/utils/functions/toaster";
import { useAppShell } from "@/components/layouts/app-shell-context";

const NavBar: React.FC = () => {
    const { organization } = useContext(OrganizationContext);
    const location = useLocation();
    const name = getName();
    const [orgConfig, setOrgConfig] = useState<any>();
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [switchOrgModalOpen, setSwitchOrgModalOpen] = useState(false);
    const [openSwitchOrgAfterClose, setOpenSwitchOrgAfterClose] = useState(false);
    const openSwitchOrgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const {
        mobileSidebarOpen,
        setMobileSidebarOpen,
        toggleSidebarCollapsed,
        isMobile,
        isTablet,
        sidebarCollapsed,
    } = useAppShell();

    useEffect(() => {
        setMobileSidebarOpen(false);
    }, [location.pathname, setMobileSidebarOpen]);

    useEffect(() => {
        if (!profileMenuOpen && openSwitchOrgAfterClose) {
            if (openSwitchOrgTimerRef.current) {
                clearTimeout(openSwitchOrgTimerRef.current);
            }
            openSwitchOrgTimerRef.current = setTimeout(() => {
                setSwitchOrgModalOpen(true);
                setOpenSwitchOrgAfterClose(false);
                openSwitchOrgTimerRef.current = null;
            }, 0);
        }
    }, [profileMenuOpen, openSwitchOrgAfterClose]);

    useEffect(() => {
        return () => {
            if (openSwitchOrgTimerRef.current) {
                clearTimeout(openSwitchOrgTimerRef.current);
            }
        };
    }, []);

    const handleOpenChange = (open: boolean) => {
        setProfileMenuOpen(open);
        if (!open) {
            return;
        }
        fetchConfig(
            null,
            (res: any) => {
                setOrgConfig(res);
            },
            (err: any) => {
                errorToasterMessage(err?.response?.data?.message);
            }
        );
    };

    const orgLabel =
        organization && organization?.name !== "GLOBAL"
            ? organization?.name
            : "Personal";

    const showMenuToggle = isMobile || isTablet;

    const handleMenuToggle = () => {
        if (isMobile) {
            setMobileSidebarOpen(!mobileSidebarOpen);
            return;
        }
        toggleSidebarCollapsed();
    };

    return (
        <header className="gs-app-header">
            <div className="gs-app-header-inner">
                <div className="gs-app-header-brand">
                    {showMenuToggle && (
                        <button
                            type="button"
                            className={cn(
                                "gs-app-menu-btn nk-quick-nav-icon",
                                isMobile && mobileSidebarOpen && "active"
                            )}
                            onClick={handleMenuToggle}
                            aria-label={
                                isMobile
                                    ? mobileSidebarOpen
                                        ? "Close navigation menu"
                                        : "Open navigation menu"
                                    : sidebarCollapsed
                                      ? "Expand sidebar"
                                      : "Collapse sidebar"
                            }
                            aria-expanded={isMobile ? mobileSidebarOpen : !sidebarCollapsed}
                        >
                            <em id="navbar-menu-icon" className="icon ni ni-menu" />
                        </button>
                    )}
                    <Link to="/dashboard" className="gs-app-header-logo-link">
                        <img
                            id="navbar-logo"
                            src="/images/logo.png"
                            srcSet="/images/logo.png"
                            alt="GreenSign"
                            width={100}
                            className="h-auto max-w-[88px] sm:max-w-[100px]"
                        />
                    </Link>
                    <p
                        id="navbar-portal-name"
                        className="gs-app-header-portal-name m-0 hidden min-[375px]:inline"
                    >
                        Admin
                    </p>
                </div>
                <div className="gs-app-header-tools nk-header-tools">
                    <ul className="nk-quick-nav">
                        <li>
                            <span
                                id="navbar-organization-name"
                                className="gs-app-header-org-name hidden sm:inline"
                            >
                                {orgLabel}
                            </span>
                            <DropdownMenu open={profileMenuOpen} onOpenChange={handleOpenChange}>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        className="gs-app-header-avatar-trigger"
                                        id="dropdown-basic"
                                        aria-label="Open profile menu"
                                    >
                                        <Avatar id="navbar" name={name} />
                                    </button>
                                </DropdownMenuTrigger>
                                <ProfileDropdown
                                    orgConfig={orgConfig}
                                    onSwitchProfile={() => {
                                        setProfileMenuOpen(false);
                                        setOpenSwitchOrgAfterClose(true);
                                    }}
                                />
                            </DropdownMenu>
                        </li>
                    </ul>
                </div>
            </div>
            <SwitchOrganizationModal
                show={switchOrgModalOpen}
                onHide={() => {
                    if (openSwitchOrgTimerRef.current) {
                        clearTimeout(openSwitchOrgTimerRef.current);
                        openSwitchOrgTimerRef.current = null;
                    }
                    setOpenSwitchOrgAfterClose(false);
                    setSwitchOrgModalOpen(false);
                }}
            />
        </header>
    );
};

export default NavBar;
